import { Routes, Route, Navigate } from "react-router-dom";
import { useAdvisorStore } from "@/features/auth/stores/advisorStore";
import { useAdminStore } from "@/features/auth/stores/adminStore";

// Layouts
import { DashboardLayout } from "@/shared/components/layout/DashboardLayout";
import { ErrorBoundary } from "@/shared/components/ui/ErrorBoundary";
import { DocumentTitleTracker } from "@/shared/hooks";

// Pages
import { LoginPage } from "@/pages/LoginPage";
import { RegisterPage } from "@/pages/RegisterPage";
import { AdminLoginPage } from "@/pages/AdminLoginPage";
import { DashboardPage } from "@/pages/DashboardPage";
import { LeadsPage } from "@/pages/LeadsPage";
import { SessionsPage } from "@/pages/SessionsPage";
import { SessionDetailPage } from "@/pages/SessionDetailPage";
import { ProfilePage } from "@/pages/ProfilePage";
import { TemplatesPage } from "@/pages/TemplatesPage";
import { TicketsPage } from "@/pages/TicketsPage";
import { AdminDashboardPage } from "@/pages/AdminDashboardPage";
import { AdminAdvisorsPage } from "@/pages/AdminAdvisorsPage";
import { AuditLogsPage } from "@/pages/AuditLogsPage";
import { PublicAdvisorPage } from "@/pages/PublicAdvisorPage";

// Protected route component
function ProtectedRoute({ children, requireAdmin = false }: { children: React.ReactNode; requireAdmin?: boolean }) {
  const { isAuthenticated: isAdvisorAuth } = useAdvisorStore();
  const { isAuthenticated: isAdminAuth } = useAdminStore();

  if (requireAdmin) {
    if (!isAdminAuth) {
      return <Navigate to="/admin/login" replace />;
    }
    return <>{children}</>;
  }

  if (!isAdvisorAuth) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

function App() {
  return (
    <ErrorBoundary>
      <DocumentTitleTracker />
      <Routes>
        {/* Public routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/admin/login" element={<AdminLoginPage />} />
      <Route path="/doradca/:slug" element={<PublicAdvisorPage />} />

      {/* Protected advisor routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<DashboardPage />} />
        <Route path="leads" element={<LeadsPage />} />
        <Route path="sessions" element={<SessionsPage />} />
        <Route path="sessions/:id" element={<SessionDetailPage />} />
        <Route path="templates" element={<TemplatesPage />} />
        <Route path="tickets" element={<TicketsPage />} />
        <Route path="profile" element={<ProfilePage />} />
      </Route>

      {/* Protected admin routes */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute requireAdmin>
            <DashboardLayout isAdmin />
          </ProtectedRoute>
        }
      >
        <Route index element={<AdminDashboardPage />} />
        <Route path="advisors" element={<AdminAdvisorsPage />} />
        <Route path="audit-logs" element={<AuditLogsPage />} />
      </Route>

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </ErrorBoundary>
  );
}

export default App;
